import { Vector3 } from 'three/webgpu';
import { caveGap } from '../../engine/caves/gap';
import { hashSeed } from '../../engine/math/rng';

interface Placeable {
  id: string;
  hollows: { center: [number, number, number]; radius: number }[];
  links: { from: number; to: number; axis: [number, number, number]; gap?: number }[];
}

/**
 * The ground around a cave mouth: flat out to `flat` metres, blending back into the natural
 * terrain over the next `blend`. Small hollows get smaller plateaus: a full-size one would
 * flatten much of a tiny world.
 */
export function plateauSize(radius: number): { flat: number; blend: number } {
  return { flat: Math.min(45, 0.1 * radius), blend: Math.min(110, 0.22 * radius) };
}

/**
 * Where each hollow of a cave world sits: the first keeps its own centre, and each link
 * puts its `to` hollow along its axis from `from`, `gaps[i]` metres of rock apart (between
 * their sea-level walls). By default the gaps come from the worlds' sizes (linkGaps).
 */
export function placeHollows(def: Placeable, gaps = linkGaps(def)): Vector3[] {
  const H = def.hollows;
  const centers = H.map(() => new Vector3());
  centers[0].set(...H[0].center);
  const placed = H.map((_, i) => i === 0);
  def.links.forEach((l, i) => {
    if (!placed[l.from] || placed[l.to])
      throw new Error(`cave ${def.id}: links must grow a tree from hollow 0`);
    const axis = new Vector3(...l.axis).normalize();
    centers[l.to].copy(centers[l.from]).addScaledVector(axis, H[l.from].radius + gaps[i] + H[l.to].radius);
    placed[l.to] = true;
  });
  return centers;
}

/** The rock between the two worlds of each link (m): its own `gap`, or see caveGap. */
export function linkGaps(def: Placeable): number[] {
  const base = stringSeed(def.id);
  return def.links.map(
    (l, i) => l.gap ?? caveGap(def.hollows[l.from].radius, def.hollows[l.to].radius, hashSeed(base, i, 17)),
  );
}

/** The seed of link i's cave interior. */
export function linkSeed(def: Placeable, i: number, variant = 0): number {
  return hashSeed(stringSeed(def.id), i, variant);
}

/** A stable number for a string (FNV-1a). */
export function stringSeed(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 0x01000193) >>> 0;
  return h;
}
